import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { employeeActions } from "../../redux/actions";

const COLORS = ["#3b82f6", "#f97316", "#22c55e", "#e11d48"];

export default function Dashboard() {
  const dispatch = useDispatch();
  const stats = useSelector((s) => s.employeeReducer.stats);

  useEffect(() => {
    dispatch(employeeActions.getDashboardStats());
  }, []);

  // destructure for ease
  const {
    totalEmployees = 0,
    upcomingExpiries = {},
    alertsSummary = {},
    alertDaysBefore = 30,
  } = stats || {};

  // data for bar/pie charts
  const chartData = [
    {
      name: "Passport",
      value: alertsSummary.passport_expiring || 0,
    },
    {
      name: "Visa",
      value: alertsSummary.visa_expiring || 0,
    },
    {
      name: "Contract",
      value: alertsSummary.contract_expiring || 0,
    },
    {
      name: "BRP",
      value: alertsSummary.brp_expiring || 0,
    },
  ];

  // flatten to list for table
  const upcomingList = [
    ...(upcomingExpiries.passport || []).map((x) => ({
      ...x,
      type: "Passport",
      date: x.passport_expiry_date,
    })),
    ...(upcomingExpiries.visa || []).map((x) => ({
      ...x,
      type: "Visa",
      date: x.visa_expiry_date,
    })),
    ...(upcomingExpiries.contract || []).map((x) => ({
      ...x,
      type: "Contract",
      date: x.contract_end_date,
    })),
    ...(upcomingExpiries.brp || []).map((x) => ({
      ...x,
      type: "BRP",
      date: x.brp_expiry_date,
    })),
  ]
    // sort nearest first
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 8); // show top 8

  return (
    <div className="container-fluid py-2">
      {/* header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="mb-0">Compliance Dashboard</h3>
          <small className="text-muted">
            Upcoming expiries in next {alertDaysBefore} day(s)
          </small>
        </div>
      </div>

      {/* stats cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <p className="text-muted mb-1">Total Employees</p>
              <h3 className="fw-bold">{totalEmployees}</h3>
              <small className="text-success">All active employees</small>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <p className="text-muted mb-1">Passport Expiring</p>
              <h3 className="fw-bold">
                {alertsSummary.passport_expiring || 0}
              </h3>
              <small className="text-muted">
                Next {alertDaysBefore} days
              </small>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <p className="text-muted mb-1">Visa Expiring</p>
              <h3 className="fw-bold">{alertsSummary.visa_expiring || 0}</h3>
              <small className="text-muted">
                Next {alertDaysBefore} days
              </small>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <p className="text-muted mb-1">Contracts / BRP</p>
              <h3 className="fw-bold">
                {(alertsSummary.contract_expiring || 0) +
                  (alertsSummary.brp_expiring || 0)}
              </h3>
              <small className="text-muted">
                Contract: {alertsSummary.contract_expiring || 0} • BRP:{" "}
                {alertsSummary.brp_expiring || 0}
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* charts + table row */}
      <div className="row g-4">
        {/* left: chart(s) */}
        <div className="col-lg-7">
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white border-0 d-flex justify-content-between">
              <h6 className="mb-0">Document Expiries</h6>
              <small className="text-muted">
                Next {alertDaysBefore} days
              </small>
            </div>
            <div className="card-body" style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" name="Count">
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % 4]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* optional: pie */}
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white border-0">
              <h6 className="mb-0">Expiry Breakdown</h6>
              <small className="text-muted">Share by type</small>
            </div>
            <div className="card-body d-flex justify-content-center" style={{ height: 240 }}>
              <ResponsiveContainer width="60%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={80}
                    label
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* right: upcoming list */}
        <div className="col-lg-5">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white border-0 d-flex justify-content-between">
              <div>
                <h6 className="mb-0">Upcoming Expiries</h6>
                <small className="text-muted">
                  Nearest {alertDaysBefore} days
                </small>
              </div>
              <span className="badge bg-primary d-flex align-items-center">
                {upcomingList.length} found
              </span>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Employee</th>
                      <th>Type</th>
                      <th>Expiry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingList.length === 0 && (
                      <tr>
                        <td colSpan={3} className="text-center py-4 text-muted">
                          No documents expiring soon.
                        </td>
                      </tr>
                    )}
                    {upcomingList.map((item) => (
                      <tr key={`${item.id}-${item.type}`}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {/* avatar circle (optional) */}
                            <div
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: "50%",
                                background: "#e2e8f0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 12,
                                fontWeight: 600,
                                textTransform: "uppercase",
                              }}
                            >
                              {(item.fullName || "?").slice(0, 2)}
                            </div>
                            <div>
                              <div className="fw-semibold">
                                {item.fullName || "—"}
                              </div>
                              <div className="text-muted" style={{ fontSize: 12 }}>
                                {item.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="badge bg-light text-dark">
                            {item.type}
                          </span>
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <span className="text-danger fw-semibold" style={{ fontSize: 13 }}>
                            {item.date}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* <div className="card-footer bg-white border-0 text-end">
              <button className="btn btn-sm btn-outline-primary">
                View All Employees
              </button>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
}
